import { Box } from "@rebass/grid";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as contractActions from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.actions";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import {
  isRequestingRentioMoveService,
  requestRentioMoveServiceError,
} from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.selectors";
import { EContractFetchType } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.types";
import * as contractUtils from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.utils";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  ESpacings,
  Loading,
  Modal,
  MultiStepForm,
  OptionListItem,
  SuccessModal,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import React from "react";
import { useDispatch } from "react-redux";
import { requestRentioMoveServiceExitSchema } from "../../forms/requestRentioMoveServiceExit";
import {
  performingSubmitActionResultSelector,
  submitActionCreator,
} from "../../forms/requestRentioMoveServiceExit/schema.requestRentioMoveServiceExit.actions";
import { ts } from "../../services";

const { i18n } = utils;

export enum EEnergyWaterExitOption {
  FinalInvoices = "FINAL_INVOICES",
  VacancyPropertyContract = "VACANCY_PROPERTY_CONTRACT",
}

enum EStep {
  Choice,
  FinalInvoicesInfo,
  VacancyContractForm,
  Outro,
}

interface IProps {
  property: IProperty;
  contract: IContract;
  onClose: () => void;
}

export const EnergyWaterExitModal = (props: IProps) => {
  const { property, contract, onClose } = props;
  const dispatch = useDispatch();

  const [formId] = React.useState(generateFormId());
  const [step, setStep] = React.useState<EStep>(EStep.Choice);
  const [selectedOption, setSelectedOption] =
    React.useState<EEnergyWaterExitOption | null>(null);
  const [isRequestingFinalInvoice, setIsRequestingFinalInvoice] =
    React.useState(false);

  const activeContractsForPropertyFilter = contractUtils.getFilterForProperty(
    property.id,
    EContractFetchType.Active,
  );

  const endedContractsForPropertyFilter = contractUtils.getFilterForProperty(
    property.id,
    EContractFetchType.Ended,
  );

  const { items: activeContracts = [], isFetching: isFetchingActiveContracts } =
    contractHooks.useGetAll({
      shouldRefetch: true,
      query: {
        filter: activeContractsForPropertyFilter,
        sort: [{ field: "startDate", method: "DESC" }],
      },
    });

  const { items: endedContracts = [], isFetching: isFetchingEndedContracts } =
    contractHooks.useGetAll({
      shouldRefetch: true,
      query: {
        filter: endedContractsForPropertyFilter,
        sort: [{ field: "startDate", method: "DESC" }],
      },
    });

  const getPreviousContract = () => {
    if (activeContracts?.length > 1) {
      const otherActiveContracts = activeContracts.filter(
        otherContract => otherContract?.id !== contract?.id,
      );

      const previousActive = otherActiveContracts
        .filter(c => new Date(c.startDate) < new Date(contract.startDate))
        .sort(
          (a, b) =>
            new Date(contract.startDate).getTime() -
            new Date(a.startDate).getTime() -
            (new Date(contract.startDate).getTime() -
              new Date(b.startDate).getTime()),
        );

      return previousActive[0];
    }

    if (endedContracts?.length > 0) {
      const previousEnded = endedContracts
        .filter(
          endedContract =>
            new Date(endedContract.stopDate || "") <
            new Date(contract.startDate),
        )
        .sort(
          (a, b) =>
            new Date(contract.startDate).getTime() -
            new Date(a.stopDate || "").getTime() -
            (new Date(contract.startDate).getTime() -
              new Date(b.stopDate || "").getTime()),
        );

      return previousEnded[0];
    }

    return undefined;
  };

  const isLoading = isFetchingActiveContracts || isFetchingEndedContracts;

  const handleNextFromChoice = () => {
    if (selectedOption === EEnergyWaterExitOption.FinalInvoices) {
      setStep(EStep.FinalInvoicesInfo);
    } else if (
      selectedOption === EEnergyWaterExitOption.VacancyPropertyContract
    ) {
      setStep(EStep.VacancyContractForm);
    }
  };

  const handleRequestFinalInvoice = () => {
    setIsRequestingFinalInvoice(true);
    dispatch(
      contractActions.requestFinalInvoiceStart.getAction({
        id: contract.id,
        onSuccess: () => {
          setIsRequestingFinalInvoice(false);
          onClose();
        },
        onFailure: () => {
          setIsRequestingFinalInvoice(false);
        },
      }),
    );
  };

  const renderChoice = () => (
    <Modal
      width="medium"
      onClose={onClose}
      heading={getLocalizedText("exit_flow.energy_water.modal.heading")}
      actions={[
        {
          content: ts.system("previous"),
          onClick: onClose,
          appearance: "outline",
        },
        {
          content: ts.system("next"),
          onClick: handleNextFromChoice,
          appearance: "primary",
          isDisabled: !selectedOption,
        },
      ]}
    >
      {isLoading ? (
        <Box mb={ESpacings.base}>
          <Loading />
        </Box>
      ) : (
        <Box>
          <Box mb={ESpacings.base}>
            {getLocalizedText("exit_flow.energy_water.modal.question")}
          </Box>
          <OptionListItem
            type="radio"
            title={getLocalizedText(
              "exit_flow.energy_water.option.final_invoices",
            )}
            selected={selectedOption === EEnergyWaterExitOption.FinalInvoices}
            onChange={() =>
              setSelectedOption(EEnergyWaterExitOption.FinalInvoices)
            }
          />
          <Spacer weight={ESpacerWeight.W08} />
          <OptionListItem
            type="radio"
            title={getLocalizedText(
              "exit_flow.energy_water.option.vacancy_property_contract",
            )}
            selected={
              selectedOption === EEnergyWaterExitOption.VacancyPropertyContract
            }
            onChange={() =>
              setSelectedOption(EEnergyWaterExitOption.VacancyPropertyContract)
            }
          />
        </Box>
      )}
    </Modal>
  );

  const renderFinalInvoicesInfo = () => (
    <Modal
      width="medium"
      onClose={onClose}
      heading={getLocalizedText(
        "exit_flow.energy_water.final_invoices.heading",
      )}
      actions={[
        {
          content: ts.system("previous"),
          onClick: () => setStep(EStep.Choice),
          appearance: "outline",
        },
        {
          content: ts.system("check"),
          onClick: handleRequestFinalInvoice,
          appearance: "primary",
          isDisabled: isRequestingFinalInvoice,
        },
      ]}
    >
      {i18n.tm("exit_flow.energy_water.final_invoices.content", {
        markdownProps: { listAsChecklist: true },
      })}
    </Modal>
  );

  const renderVacancyContractForm = () => {
    return (
      <MultiStepForm
        formId={`request-move-service-exit-${formId}`}
        schemas={requestRentioMoveServiceExitSchema({
          property,
          contract,
          previousContract: getPreviousContract(),
        })}
        asModal={true}
        withAside={false}
        onPressPreviousFirstStep={() => {
          setStep(EStep.Choice);
        }}
        modalProps={{
          onClose,
          heading: getLocalizedText(
            "exit_flow.energy_water.vacancy_contract.heading",
          ),
          width: "medium",
        }}
        performingSubmitActionResultSelector={performingSubmitActionResultSelector(
          contract.id,
        )}
        isPerformingSubmitActionSelector={state =>
          isRequestingRentioMoveService(state, contract.id)
        }
        performingSubmitActionErrorSelector={state =>
          requestRentioMoveServiceError(state, contract.id)
        }
        submitActionCreator={submitActionCreator({
          property,
          contract,
        })}
        onSuccess={() => setStep(EStep.Outro)}
        submitLabel={getLocalizedText("system.submit")}
      />
    );
  };

  const renderOutro = () => {
    return (
      <SuccessModal
        width="medium"
        onClose={onClose}
        actions={[
          {
            content: getLocalizedText("system.check"),
            onClick: onClose,
            appearance: "primary",
          },
        ]}
      >
        {i18n.tm("request_rentio_move_service.modal.outro.content")}
      </SuccessModal>
    );
  };

  switch (step) {
    case EStep.FinalInvoicesInfo:
      return renderFinalInvoicesInfo();

    case EStep.VacancyContractForm:
      return renderVacancyContractForm();

    case EStep.Outro:
      return renderOutro();

    case EStep.Choice:
    default:
      return renderChoice();
  }
};
